import { Button } from '@treams/treams-ui';
import { ChevronLeftIcon } from '@treams/treams-icons';
import * as graphql from 'generated/graphql';
import { t } from 'i18next';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';
import { rgba } from 'polished';
import { PropsWithChildren, useState, useEffect } from 'react';
import { Box } from 'reflexbox';
import styled, { css } from 'styled-components';
import EvaluationSubmissionSidebar from 'conversations/evaluation-submission/EvaluationSubmissionSidebar';

const SidebarContainer = styled(Box)`
	width: 300px;
	position: absolute;
	right: 0;
	top: 0;
	height: 100%;
	pointer-events: none;
	overflow-x: clip;
`;

const Sidebar = styled(Box)<{ open?: boolean }>`
	${({ theme, open }) => css`
		position: sticky;
		z-index: 2;
		top: 0;
		width: 300px;
		height: 100vh;
		max-width: 100vw;
		background: ${theme.colors.whiteBlue};
		border-left: 1px solid ${theme.colors.lightBlue};
		box-shadow: -4px 0px 8px ${rgba(theme.colors.slateBlue, 0.1)};
		transition: 1s cubic-bezier(0, 1, 0, 1) transform;
		transform: ${open ? 'none' : 'translateX(100%)'};
		pointer-events: auto;
	`}
`;

const SideFloatingButton = styled(Button)<{ open?: boolean }>`
	position: absolute;
	top: 0px;
	left: 0px;
	border-radius: 6px 6px 0 0;
	transition: 1s cubic-bezier(0, 1, 0, 1) opacity;
	transform: rotate(-90deg) translateY(-100%) translateX(-100%) translateX(-24px);
	transform-origin: top left;
	${(props) =>
		props.open &&
		css`
			opacity: 0;
			pointer-events: none;
		`};
`;

export default function EvaluationSubmissionSidebarLayout(
	props: PropsWithChildren<{
		id: string;
		user: graphql.User;
		isSideBarOpen: boolean;
		onSidebarOpen: (open: boolean) => void;
	}>,
) {
	const [isSidebarOpen, setIsSidebarOpen] = useState(props.isSideBarOpen);
	const hasConversationContext = useFeature(graphql.FeatureName.EvaluationsContext);

	const { onSidebarOpen } = props;

	useEffect(() => {
		onSidebarOpen(isSidebarOpen);
	}, [isSidebarOpen, onSidebarOpen]);

	return (
		<StyledBox>
			{hasConversationContext && (
				<SidebarContainer>
					<Sidebar open={isSidebarOpen}>
						<EvaluationSubmissionSidebar
							onClose={() => {
								setIsSidebarOpen(false);
							}}
							conversationID={props.id}
							user={props.user}
						/>
						<SideFloatingButton
							open={isSidebarOpen}
							icon={
								<Box style={{ transform: 'rotate(90deg)' }}>
									<ChevronLeftIcon />
								</Box>
							}
							title={t('conversation.sidebar.context.openButton.title')}
							type="secondary"
							onClick={() => {
								setIsSidebarOpen(true);
								window.scrollTo(0, 0);
							}}
						/>
					</Sidebar>
				</SidebarContainer>
			)}

			{props.children}
		</StyledBox>
	);
}

const StyledBox = styled(Box)`
	position: relative;
`;
